import React from "react";
import moment from "moment";
import { normalizeUserRole } from "./permissions";
import { formatDate } from "./formatDate";

export const setToken = (token: string) => {
  // set to local storage
  localStorage.setItem("token", token);
};

export const setUser = (user: any) => {
  // Normalize backend role to frontend role (e.g., "A" -> "Admin")
  if (user?.type) {
    const normalizedRole = normalizeUserRole(user.type);
    if (normalizedRole) {
      user.type = normalizedRole;
    }
  }
  // set to local storage
  localStorage.setItem("user", JSON.stringify(user));
};

export const getUser = () => {
  // get from local storage
  const user = localStorage.getItem("user");
  // Handle edge case where "undefined" or "null" string was stored
  if (!user || user === "undefined" || user === "null") {
    return null;
  }
  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
};

/**
 * Check if the current user is a Marketing Only user
 * @returns true if user type is "MarketingOnly", false otherwise
 */
export const isMarketingOnlyUser = (): boolean => {
  const user = getUser();
  return user?.type === "MarketingOnly";
};

export const isAllInOneUser = (): boolean => {
  const user = getUser();
  return user?.type !== "MarketingOnly" && !!user?.googleAdsCustomerId;
};

export const clearUser = () => {
  localStorage.removeItem("user");
};
export const getConsent = () => {
  // get from local storage
  const consent = localStorage.getItem("consent");
  return consent ? JSON.parse(consent) : null;
};

export const setConsent = (consent: any) => {
  // set to local storage
  localStorage.setItem("consent", JSON.stringify(consent));
};

export function checkPasswordStrength(password: any) {
  const requirements = [
    { regex: /[a-z]/, message: "lowercase letter" },
    { regex: /[A-Z]/, message: "uppercase letter" },
    { regex: /\d/, message: "digit" },
    { regex: /[@$!%*?&]/, message: "special character" },
    { regex: /.{8,}/, message: "at least 8 characters" },
  ];
  let score = 0;
  let message = "";
  requirements.forEach((requirement) => {
    if (requirement.regex.test(password)) {
      score++;
    } else {
      message += ` ${requirement.message},`;
    }
  });
  if (score === requirements.length) {
    message = "Your password is great. Nice work!";
  } else {
    message = `Your password needs ${message.slice(0, -1)}`;
  }
  return { score: (score / requirements.length) * 100, message };
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s+@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const clearTokenAndUser = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

export const validatePhone = (phone: string): boolean => {
  return !!phone.match(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/);
};

export default function passwordValidation(password: string) {
  //   add special character
  const passwordRegex =
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
  return passwordRegex.test(password);
}

export function formatNumber(num: number, sign = "") {
  const getSign = (n: any, sign: string) => {
    if (sign === "%") {
      return `${n}%`;
    }
    if (sign === "$") {
      return `$${n}`;
    }
    return n;
  };
  // Handle null, NaN, or undefined values
  if (num === null || isNaN(num) || num === undefined) {
    return "-";
  }
  if (num === 0) {
    return "-";
  }
  const absNum = Math.abs(num);

  // Format based on new rules
  let formattedNum = "";
  if (absNum >= 1e9) {
    // Billions: use 2 decimal places
    formattedNum = (num / 1e9).toFixed(2).replace(/\.00$/, "") + "B";
  } else if (absNum >= 1e6) {
    // Millions: use 1 decimal place
    formattedNum = (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absNum >= 1e3) {
    // Thousands: always use "K" with no decimals
    formattedNum = (num / 1e3).toFixed(0).replace(/\.0$/, "") + "K";
  } else if (absNum >= 100) {
    // 100-999: full number with commas
    formattedNum = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(num);
  } else {
    // < 100: simple number format without commas
    formattedNum = num.toFixed(0).toString().replace(/\.00$/, "");
  }

  if (num < 0) {
    formattedNum = `(${getSign(formattedNum, sign)})`;
  }
  return getSign(formattedNum, sign);
}

export function commaSeparatedNumber(
  num: number,
  isPercentage = false,
  isDollar = true,
  maxFractionDigits = 0,
  minFractionDigits = 0,
  isAbove100 = false,
  currencyCode = "USD"
) {
  // Handle null, NaN, or undefined values
  if (num === null || isNaN(num) || num === undefined) {
    return "-";
  }

  // Handle zero values
  if (num === 0) {
    return "-";
  }

  // For percentages, limit to 100% maximum unless isAbove100 is true
  const processedNum =
    isPercentage && num > 100 ? (isAbove100 ? num : 100) : num;

  // Apply consistent decimal rules: zero decimals unless value is less than 1
  let actualMaxFractionDigits = maxFractionDigits;
  let actualMinFractionDigits = minFractionDigits;

  if (Math.abs(processedNum) >= 1) {
    // Numbers >= 1: always zero decimals
    actualMaxFractionDigits = 0;
    actualMinFractionDigits = 0;
  } else if (Math.abs(processedNum) < 1 && Math.abs(processedNum) > 0) {
    // Numbers < 1: allow 1 decimal place only if specified
    actualMaxFractionDigits = Math.min(1, maxFractionDigits);
  }

  // Format the number with commas for thousands
  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: actualMinFractionDigits,
    maximumFractionDigits: actualMaxFractionDigits,
  };
  const formattedNum = new Intl.NumberFormat("en-US", formatOptions).format(
    processedNum
  );

  // If the formatted number is 0, return "-"
  if (Number(formattedNum.replace(/,/g, "")) === 0) {
    return "-";
  }

  // Symbol and its spacing vary by currency (MX$5,000 vs HUF 5,000), so let
  // Intl place it rather than prefixing "$". Unknown code falls back to USD.
  const asMoney = () => {
    try {
      return new Intl.NumberFormat("en-US", {
        ...formatOptions,
        style: "currency",
        currency: currencyCode,
      }).format(processedNum);
    } catch {
      console.warn(`Invalid currency code "${currencyCode}", falling back to $`);
      return `$${formattedNum}`;
    }
  };

  const value = isPercentage
    ? `${formattedNum}%`
    : isDollar
    ? asMoney()
    : formattedNum;

  return processedNum < 0 ? value.replace("-", "") : value;
}

// Money in the currency the value is actually denominated in.
export const money = (value: number, currencyCode = "USD") =>
  commaSeparatedNumber(value, false, true, 0, 0, false, currencyCode);

/**
 * Marker appended to occupancy figures above 100%. Occupancy displays cap the shown
 * percentage at 100%, so the asterisk flags that the raw (uncapped) occupancy exceeded
 * full and the displayed value was clamped. Returns "" for values of 100% or below, or
 * non-numeric input.
 */
export function occupancyOverflowMarker(
  occupancy: number | null | undefined,
): string {
  if (occupancy === null || occupancy === undefined || isNaN(occupancy)) {
    return "";
  }
  return occupancy > 100 ? "*" : "";
}

export function getInitial(firstName: string, lastName: string) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`;
  } else if (firstName) {
    return firstName[0];
  } else if (lastName) {
    return lastName[0];
  } else {
    return "";
  }
}

export function convertCentToDollar(cent: number) {
  return cent / 100;
}

export function convertDollarToCent(dollar: number) {
  return dollar * 100;
}

export function calcCrow(lat1, lng1, lat2, lng2) {
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const R = 3958.8; // Radius of the Earth in miles
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return (R * c).toFixed(2); // Distance in miles
}

// Converts numeric degrees to radians
function toRad(Value) {
  return (Value * Math.PI) / 180;
}

export function getNumberWithSign(num: number, isPercentage = false) {
  // Handle null, NaN, or undefined values
  if (num === null || isNaN(num) || num === undefined) {
    return "-";
  }

  if (num === 0) {
    return "-";
  }
  if (num > 0) {
    return `+${commaSeparatedNumber(num, isPercentage)}`;
  }
  if (num < 0) {
    return `${commaSeparatedNumber(num, isPercentage)}`;
  }
  return "-";
}

export function getPercentage(num: number) {
  // Handle null, NaN, or undefined values
  if (num === null || isNaN(num) || num === undefined) {
    return "-";
  }

  if (num && Number(num) === 0) {
    return "-";
  }
  if (num && Number(num) > 100) {
    return 100;
  }
  // Zero decimals unless value is less than 1
  if (num < 1 && num > 0) {
    return Number(num.toFixed(1));
  }
  return Math.round(num);
}

export const getHeightInVh = (height: number) => {
  const modelHeight = 853;
  return `${(height / modelHeight) * 100}vh`;
};

export const getDate = (date: string) => {
  // format: 2024-01-01
  // return: Date object for Safari compatibility
  if (!date) return null;
  return moment(date, "YYYY-MM-DD").toDate();
};

/**
 * Format date to M/D/YYYY format (e.g., "1/15/2024").
 *
 * Thin wrapper over the shared `formatDate(..., "numeric")` utility (audit
 * UX-05) — kept as a named export so existing call sites don't churn. Behaviour
 * is identical: M/D/YYYY for valid input, "" for empty/invalid.
 * @param date - Date string, moment object, or Date
 * @returns Formatted date string in M/D/YYYY format
 */
export const formatDisplayDate = (date: string | moment.Moment | Date): string =>
  formatDate(date, "numeric");

const FUNNEL_EVENT_LABELS: Record<string, string> = {
  ATC: "Add to Cart",
  BCO: "Begin Checkout",
};

export function expandFunnelEventLabel(event: string): string {
  return FUNNEL_EVENT_LABELS[event] ?? event;
}

export function formatTimePerVisit(seconds: number | undefined): string {
  if (!seconds || seconds < 1) return "<1s";
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

const METRIC_SPLIT = /(\$[\d,]+|[\d,]+\.?\d*%?|\d{1,3}(?:,\d{3})+)/g;
const METRIC_TEST = /^\$[\d,]+$|^[\d,]+\.?\d*%?$|^\d{1,3}(?:,\d{3})+$/;

export function highlightMetrics(text: string): React.ReactNode {
  const parts = text.split(METRIC_SPLIT);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    METRIC_TEST.test(part)
      ? React.createElement("span", { key: i, className: "font-semibold text-content-primary" }, part)
      : part,
  );
}

const CURRENCY_RANGE = /\$([0-9,]+(?:\.\d+)?)\s*[–—-]\s*\$([0-9,]+(?:\.\d+)?)/g;

export function averageCurrencyRanges(text: string): string {
  return text.replace(CURRENCY_RANGE, (_, lowStr, highStr) => {
    const low = parseFloat(lowStr.replace(/,/g, ""));
    const high = parseFloat(highStr.replace(/,/g, ""));
    const avg = Math.round((low + high) / 2);
    return `~$${avg.toLocaleString()}`;
  });
}
